(() => {
    'use strict';

    const ROOT_SELECTOR = '[data-collection]';
    const instances = new WeakMap();

    const focusableSelector = [
        'a[href]',
        'button:not([disabled])',
        'input:not([disabled]):not([type="hidden"])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        '[tabindex]:not([tabindex="-1"])'
    ].join(',');

    class CollectionPage {
        constructor(root) {
            this.root = root;
            this.abortController = new AbortController();
            this.requestController = null;

            this.backdrop = null;
            this.drawerTrigger = null;
            this.lastFocusedElement = null;

            this.handleRootClick =
                this.handleRootClick.bind(this);

            this.handleRootChange =
                this.handleRootChange.bind(this);

            this.handleRootSubmit =
                this.handleRootSubmit.bind(this);

            this.handleDocumentKeydown =
                this.handleDocumentKeydown.bind(this);

            this.handleBackdropClick =
                this.handleBackdropClick.bind(this);

            this.handlePopstate =
                this.handlePopstate.bind(this);

            this.init();
        }

        init() {
            const signal =
                this.abortController.signal;

            this.root.addEventListener(
                'click',
                this.handleRootClick,
                { signal }
            );

            this.root.addEventListener(
                'change',
                this.handleRootChange,
                { signal }
            );

            this.root.addEventListener(
                'submit',
                this.handleRootSubmit,
                { signal }
            );

            document.addEventListener(
                'keydown',
                this.handleDocumentKeydown,
                { signal }
            );

            window.addEventListener(
                'popstate',
                this.handlePopstate,
                { signal }
            );

            this.prepareBackdrop();
        }

        get form() {
            return this.root.querySelector(
                '[data-facets-form]'
            );
        }

        get drawer() {
            return this.root.querySelector(
                '[data-filters-panel]'
            );
        }

        get toggle() {
            return this.root.querySelector(
                '[data-filters-toggle]'
            );
        }

        get isDrawerOpen() {
            return Boolean(
                this.drawer?.classList.contains(
                    'is-open'
                )
            );
        }

        prepareBackdrop() {
            const nextBackdrop =
                this.root.querySelector(
                    '[data-filters-backdrop]'
                );

            if (!nextBackdrop) {
                if (this.backdrop) {
                    this.backdrop.removeEventListener(
                        'click',
                        this.handleBackdropClick
                    );

                    this.backdrop.remove();
                    this.backdrop = null;
                }

                return;
            }

            if (
                this.backdrop &&
                this.backdrop !== nextBackdrop
            ) {
                this.backdrop.removeEventListener(
                    'click',
                    this.handleBackdropClick
                );

                this.backdrop.remove();
            }

            this.backdrop =
                nextBackdrop;

            if (
                this.backdrop.parentElement !==
                document.body
            ) {
                document.body.appendChild(
                    this.backdrop
                );
            }

            this.backdrop.removeEventListener(
                'click',
                this.handleBackdropClick
            );

            this.backdrop.addEventListener(
                'click',
                this.handleBackdropClick
            );
        }

        handleRootClick(event) {
            const toggle =
                event.target.closest(
                    '[data-filters-toggle]'
                );

            if (toggle) {
                event.preventDefault();

                this.openDrawer(
                    toggle
                );

                return;
            }

            const close =
                event.target.closest(
                    '[data-filters-close]'
                );

            if (close) {
                event.preventDefault();

                this.closeDrawer();

                return;
            }

            const filterLink =
                event.target.closest(
                    '[data-facet-chip], [data-facets-clear]'
                );

            if (filterLink) {
                event.preventDefault();

                this.closeDrawer({
                    restoreFocus: false
                });

                this.applyFacets(
                    filterLink.href,
                    {
                        pushState: true,
                        focusResults: true
                    }
                );

                return;
            }

            const loadMore =
                event.target.closest(
                    '[data-load-more]'
                );

            if (loadMore) {
                event.preventDefault();

                this.loadMore(
                    loadMore
                );
            }
        }

        handleRootChange(event) {
            const target =
                event.target;

            if (
                !target.matches(
                    '[data-facet-input]'
                )
            ) {
                return;
            }

            /*
              Sorting updates immediately.
      
              Drawer filter inputs do NOT trigger
              AJAX here. The shopper can select
              multiple filters and then press Apply.
            */
            if (
                target.matches(
                    '[data-sort-select]'
                )
            ) {
                this.applyFacets(
                    null,
                    {
                        pushState: true,
                        focusResults: true
                    }
                );
            }
        }

        handleRootSubmit(event) {
            const form =
                event.target.closest(
                    '[data-facets-form]'
                );

            if (!form) {
                return;
            }

            event.preventDefault();

            this.closeDrawer({
                restoreFocus: false
            });

            this.applyFacets(
                null,
                {
                    pushState: true,
                    focusResults: true
                }
            );
        }

        openDrawer(
            trigger = this.toggle
        ) {
            const drawer =
                this.drawer;

            if (!drawer) {
                return;
            }

            this.drawerTrigger =
                trigger ||
                this.toggle;

            this.lastFocusedElement =
                document.activeElement;

            document.dispatchEvent(
                new CustomEvent(
                    'theme:overlay:open',
                    {
                        bubbles: true,
                        detail: {
                            source:
                                'collection-filters'
                        }
                    }
                )
            );

            this.prepareBackdrop();

            if (this.backdrop) {
                this.backdrop.hidden =
                    false;

                requestAnimationFrame(
                    () => {
                        this.backdrop?.classList.add(
                            'is-open'
                        );
                    }
                );
            }

            drawer.classList.add(
                'is-open'
            );

            drawer.setAttribute(
                'aria-hidden',
                'false'
            );

            this.toggle?.setAttribute(
                'aria-expanded',
                'true'
            );

            document.body.classList.add(
                'collection-filters-open'
            );

            const initialFocus =
                drawer.querySelector(
                    '[data-filters-close]'
                ) ||
                drawer.querySelector(
                    focusableSelector
                ) ||
                drawer;

            if (
                !drawer.hasAttribute(
                    'tabindex'
                )
            ) {
                drawer.setAttribute(
                    'tabindex',
                    '-1'
                );
            }

            requestAnimationFrame(
                () => {
                    initialFocus.focus({
                        preventScroll: true
                    });
                }
            );
        }

        closeDrawer({
            restoreFocus = true
        } = {}) {
            const drawer =
                this.drawer;

            if (!drawer) {
                return;
            }

            drawer.classList.remove(
                'is-open'
            );

            drawer.setAttribute(
                'aria-hidden',
                'true'
            );

            this.toggle?.setAttribute(
                'aria-expanded',
                'false'
            );

            this.backdrop?.classList.remove(
                'is-open'
            );

            document.body.classList.remove(
                'collection-filters-open'
            );

            const backdrop =
                this.backdrop;

            window.setTimeout(
                () => {
                    if (
                        backdrop &&
                        !backdrop.classList.contains(
                            'is-open'
                        )
                    ) {
                        backdrop.hidden =
                            true;
                    }
                },
                260
            );

            if (
                restoreFocus &&
                this.lastFocusedElement &&
                document.contains(
                    this.lastFocusedElement
                )
            ) {
                this.lastFocusedElement.focus({
                    preventScroll: true
                });
            }

            this.lastFocusedElement =
                null;

            this.drawerTrigger =
                null;
        }

        handleBackdropClick() {
            this.closeDrawer();
        }

        handleDocumentKeydown(
            event
        ) {
            if (
                !this.isDrawerOpen
            ) {
                return;
            }

            if (
                event.key ===
                'Escape'
            ) {
                event.preventDefault();

                this.closeDrawer();

                return;
            }

            if (
                event.key ===
                'Tab'
            ) {
                this.trapDrawerFocus(
                    event
                );
            }
        }

        trapDrawerFocus(
            event
        ) {
            const drawer =
                this.drawer;

            if (!drawer) {
                return;
            }

            const focusable =
                Array.from(
                    drawer.querySelectorAll(
                        focusableSelector
                    )
                ).filter(
                    (element) => {
                        return (
                            element.offsetParent !==
                            null &&
                            element.getAttribute(
                                'aria-hidden'
                            ) !==
                            'true'
                        );
                    }
                );

            if (
                !focusable.length
            ) {
                event.preventDefault();

                drawer.focus();

                return;
            }

            const first =
                focusable[0];

            const last =
                focusable[
                focusable.length - 1
                ];

            if (
                event.shiftKey &&
                document.activeElement ===
                first
            ) {
                event.preventDefault();

                last.focus();
            } else if (
                !event.shiftKey &&
                document.activeElement ===
                last
            ) {
                event.preventDefault();

                first.focus();
            }
        }

        async applyFacets(
            overrideUrl = null,
            {
                pushState = true,
                focusResults = true
            } = {}
        ) {
            const form =
                this.form;

            if (!form) {
                return;
            }

            /*
              Cancel an older filter request if
              a newer interaction happens first.
            */
            this.requestController?.abort();

            this.requestController =
                new AbortController();

            const sectionId =
                form.dataset.sectionId;

            const baseUrl =
                overrideUrl ||
                form.dataset.url ||
                window.location.href;

            const requestUrl =
                new URL(
                    baseUrl,
                    window.location.origin
                );

            /*
              Normal form submission:
              serialize the actual Shopify
              filtering and sorting controls.
            */
            if (!overrideUrl) {
                const formData =
                    new FormData(
                        form
                    );

                requestUrl.search =
                    '';

                for (
                    const [key, value]
                    of formData.entries()
                ) {
                    if (
                        String(value).trim() !==
                        ''
                    ) {
                        requestUrl.searchParams.append(
                            key,
                            value
                        );
                    }
                }
            }

            /*
              Browser URL should never contain
              section_id. The fetch URL does.
            */
            const historyUrl =
                new URL(
                    requestUrl.toString()
                );

            if (sectionId) {
                requestUrl.searchParams.set(
                    'section_id',
                    sectionId
                );
            }

            historyUrl.searchParams.delete(
                'section_id'
            );

            form.setAttribute(
                'aria-busy',
                'true'
            );

            try {
                const response =
                    await fetch(
                        requestUrl.toString(),
                        {
                            headers: {
                                Accept:
                                    'text/html'
                            },

                            signal:
                                this.requestController
                                    .signal
                        }
                    );

                if (!response.ok) {
                    throw new Error(
                        `Collection request failed (${response.status}).`
                    );
                }

                const html =
                    await response.text();

                const documentFragment =
                    new DOMParser()
                        .parseFromString(
                            html,
                            'text/html'
                        );

                const newRoot =
                    documentFragment
                        .querySelector(
                            ROOT_SELECTOR
                        );

                const newForm =
                    newRoot?.querySelector(
                        '[data-facets-form]'
                    ) ||
                    documentFragment
                        .querySelector(
                            '[data-facets-form]'
                        );

                if (!newForm) {
                    throw new Error(
                        'Updated collection form was not found.'
                    );
                }

                const currentForm =
                    this.form;

                if (!currentForm) {
                    return;
                }

                /*
                  Replace the whole mutable shopping
                  area at once. This keeps result count,
                  active chips, filters, sort selection,
                  grid, and pagination synchronized.
                */
                currentForm.replaceWith(
                    newForm
                );

                /*
                  The newly rendered backdrop begins
                  inside the new form. Move it back to
                  body so position:fixed cannot be
                  trapped by transformed ancestors.
                */
                this.prepareBackdrop();

                if (pushState) {
                    window.history.pushState(
                        {
                            collectionFacets:
                                true
                        },
                        '',
                        historyUrl.toString()
                    );
                }

                if (focusResults) {
                    const resultCount =
                        this.root.querySelector(
                            '[data-result-count]'
                        );

                    resultCount?.focus({
                        preventScroll: true
                    });
                }

                document.dispatchEvent(
                    new CustomEvent(
                        'theme:collection:update',
                        {
                            bubbles: true,
                            detail: {
                                sectionId
                            }
                        }
                    )
                );
            } catch (error) {
                if (
                    error.name ===
                    'AbortError'
                ) {
                    return;
                }

                console.error(
                    '[Collection]',
                    error
                );

                /*
                  Progressive-enhancement fallback:
                  use the real collection URL.
                */
                window.location.href =
                    historyUrl.toString();
            } finally {
                this.form?.removeAttribute(
                    'aria-busy'
                );
            }
        }

        async loadMore(
            link
        ) {
            if (
                !link ||
                link.getAttribute(
                    'aria-busy'
                ) ===
                'true'
            ) {
                return;
            }

            const gridSelector =
                link.dataset.gridSelector;

            const grid =
                gridSelector
                    ? (
                        this.root.querySelector(
                            gridSelector
                        ) ||
                        document.querySelector(
                            gridSelector
                        )
                    )
                    : null;

            if (!grid) {
                window.location.href =
                    link.href;

                return;
            }

            const pagination =
                link.closest(
                    '[data-collection-pagination]'
                );

            const status =
                pagination?.querySelector(
                    '[data-load-more-status]'
                );

            link.setAttribute(
                'aria-busy',
                'true'
            );

            try {
                const response =
                    await fetch(
                        link.href,
                        {
                            headers: {
                                Accept:
                                    'text/html'
                            }
                        }
                    );

                if (!response.ok) {
                    throw new Error(
                        `Pagination request failed (${response.status}).`
                    );
                }

                const html =
                    await response.text();

                const documentFragment =
                    new DOMParser()
                        .parseFromString(
                            html,
                            'text/html'
                        );

                const newGrid =
                    documentFragment
                        .querySelector(
                            gridSelector
                        );

                if (!newGrid) {
                    throw new Error(
                        'Next product grid was not found.'
                    );
                }

                const newProducts =
                    Array.from(
                        newGrid.children
                    );

                const fragment =
                    document.createDocumentFragment();

                newProducts.forEach(
                    (product) => {
                        fragment.appendChild(
                            product
                        );
                    }
                );

                grid.appendChild(
                    fragment
                );

                const newPagination =
                    documentFragment
                        .querySelector(
                            '[data-collection-pagination]'
                        );

                const newLink =
                    newPagination?.querySelector(
                        '[data-load-more]'
                    );

                const currentMeta =
                    pagination?.querySelector(
                        '.collection__pagination-meta'
                    );

                const newMeta =
                    newPagination?.querySelector(
                        '.collection__pagination-meta'
                    );

                if (
                    currentMeta &&
                    newMeta
                ) {
                    currentMeta.innerHTML =
                        newMeta.innerHTML;
                }

                if (newLink) {
                    link.href =
                        newLink.href;

                    link.removeAttribute(
                        'aria-busy'
                    );
                } else {
                    link.remove();
                }

                if (status) {
                    status.textContent =
                        newProducts.length === 1
                            ? '1 more product loaded'
                            : `${newProducts.length} more products loaded`;
                }

                document.dispatchEvent(
                    new CustomEvent(
                        'theme:collection:load-more',
                        {
                            bubbles: true,
                            detail: {
                                count:
                                    newProducts.length
                            }
                        }
                    )
                );
            } catch (error) {
                console.error(
                    '[Collection]',
                    error
                );

                window.location.href =
                    link.href;
            }
        }

        handlePopstate() {
            this.closeDrawer({
                restoreFocus: false
            });

            this.applyFacets(
                window.location.href,
                {
                    pushState: false,
                    focusResults: false
                }
            );
        }

        destroy() {
            this.requestController?.abort();

            this.abortController.abort();

            if (this.backdrop) {
                this.backdrop.removeEventListener(
                    'click',
                    this.handleBackdropClick
                );

                this.backdrop.remove();

                this.backdrop =
                    null;
            }

            document.body.classList.remove(
                'collection-filters-open'
            );

            instances.delete(
                this.root
            );
        }
    }

    const getRoots = (
        scope = document
    ) => {
        const roots =
            [];

        if (
            scope instanceof Element &&
            scope.matches(
                ROOT_SELECTOR
            )
        ) {
            roots.push(
                scope
            );
        }

        scope
            .querySelectorAll?.(
                ROOT_SELECTOR
            )
            .forEach(
                (root) => {
                    roots.push(
                        root
                    );
                }
            );

        return roots;
    };

    const init = (
        scope = document
    ) => {
        getRoots(
            scope
        ).forEach(
            (root) => {
                if (
                    instances.has(
                        root
                    )
                ) {
                    return;
                }

                instances.set(
                    root,
                    new CollectionPage(
                        root
                    )
                );
            }
        );
    };

    const destroyWithin = (
        scope
    ) => {
        getRoots(
            scope
        ).forEach(
            (root) => {
                instances
                    .get(
                        root
                    )
                    ?.destroy();
            }
        );
    };

    if (
        document.readyState ===
        'loading'
    ) {
        document.addEventListener(
            'DOMContentLoaded',
            () => {
                init();
            },
            {
                once: true
            }
        );
    } else {
        init();
    }

    document.addEventListener(
        'shopify:section:load',
        (event) => {
            init(
                event.target
            );
        }
    );

    document.addEventListener(
        'shopify:section:unload',
        (event) => {
            destroyWithin(
                event.target
            );
        }
    );
})();